import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Badge, Button, Dialog, IconButton, Menu, useModalFocus, useToast } from '../ui';
import {
  BookmarkIcon, ChevronDownIcon, GridIcon, LogoutIcon, MailIcon, MenuIcon, PlusIcon, ScanIcon, SearchIcon, SettingsIcon, ShieldIcon, UserIcon,
} from '../ui/icons';
import Sidebar, { Logo } from './Sidebar';
import { mayNavigate } from './changes';
import { searchable } from './searchQuery';
import { countOf, initials, ownedCollections, sharedCollections, usePendingAccounts } from './collections';
import { activeView, useSavedViews, viewLink } from './savedViews';
import { scanPath } from '../scan/scannable';
import { canCapture, opensCapture } from '../capture/capturable';

const DESKTOP = '(min-width: 1024px)';
const SEARCH_DEBOUNCE_MS = 300;

const isDesktop = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia(DESKTOP).matches;

/** Where the page is: a collection (its id), the collections home, settings, admin, or elsewhere. */
function useSection() {
  const { pathname } = useLocation();
  const collection = matchPath('/c/:id/*', pathname);
  if (collection) return { section: 'collection', collectionId: collection.params.id, onList: !collection.params['*'] };
  if (pathname === '/') return { section: 'home' };
  if (pathname === '/search') return { section: 'search' };
  if (pathname === '/settings' || pathname.startsWith('/settings/')) return { section: 'settings' };
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return { section: 'admin' };
  return { section: 'other' };
}

const typingInField = (target) => {
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
};

/**
 * The global search box. On a collection it searches that collection: the
 * text goes into the page's ?q= (the list follows it), live from the list, on
 * Enter from its other pages. Everywhere else it searches every collection
 * (phase 3 §4.1): the search page's ?q=, live after a pause once it is
 * a search the server runs (searchQuery.js: a word of two characters) (any length on Enter), the search page replacing its
 * own address as the text changes.
 */
function useShellSearch({ section, collectionId, onList, current }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlQ = searchParams.get('q') || '';
  const [value, setValue] = useState(urlQ);
  const inputRef = useRef(null);
  const lastSent = useRef(urlQ);

  const scopeKey = section === 'collection' ? `c${collectionId}` : section;
  const inCollection = section === 'collection';
  const onSearchPage = section === 'search';
  // A new page, or the query changed elsewhere (the page's own box, Back): show it.
  useEffect(() => {
    const next = inCollection || onSearchPage ? urlQ : '';
    lastSent.current = next;
    setValue(next);
  }, [scopeKey, urlQ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Through mayNavigate: a page with unsaved edits (an integration) asks first.
  const send = useCallback((text) => {
    const q = text.trim();
    lastSent.current = q;
    let to;
    let replace;
    let state;
    if (section === 'collection') {
      const params = new URLSearchParams(onList ? location.search : '');
      if (q) params.set('q', q); else params.delete('q');
      params.delete('page');
      const search = params.toString();
      to = { pathname: `/c/${collectionId}`, search: search ? `?${search}` : '' };
      replace = onList;
      // A saved view stays the one open while its search changes ("Update view").
      if (onList && location.state && location.state.savedView) state = { savedView: location.state.savedView };
    } else {
      // Emptied away from the search page: nothing to look for, stay.
      if (!q && !onSearchPage) return;
      to = { pathname: '/search', search: q ? `?q=${encodeURIComponent(q)}` : '' };
      replace = onSearchPage;
    }
    if (mayNavigate(to)) navigate(to, { replace, state });
  }, [section, collectionId, onList, onSearchPage, location.search, location.state, navigate]);

  // Live typing, after a pause: on a collection's list, on the search page,
  // and from any other page once two characters are typed. On a collection's
  // other pages (an item's form) only Enter leaves for the list.
  useEffect(() => {
    if (inCollection && !onList) return undefined;
    const q = value.trim();
    if (q === lastSent.current) return undefined;
    if (!inCollection && !onSearchPage && !searchable(q)) return undefined;
    const timer = setTimeout(() => send(value), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const here = inCollection && current;
  return {
    value,
    onChange: setValue,
    onSubmit: () => send(value),
    inputRef,
    label: here ? t('shell.searchIn', { name: current.name }) : t('shell.searchEverywhere'),
    placeholder: here ? t('shell.searchInPlaceholder', { name: current.name }) : t('shell.searchEverywherePlaceholder'),
  };
}

/**
 * The application frame (phase 2, slice 1): sidebar on desktop, top bar and
 * drawer on tablets, top bar and bottom navigation on phones. See
 * docs/design/Main.dc.html and MobileCollection.dc.html.
 */
function AppShell({ user, inventories, allInventories, onNewCollection, onInvite, onLogout, children, footer }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { section, collectionId, onList } = useSection();
  const isAdmin = user.role === 'admin';
  const [drawerOpen, setDrawerOpen] = useState(false);
  // What the drawer focuses first: its first control, or the search box ("Search", "/").
  const [drawerForSearch, setDrawerForSearch] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const drawerRef = useRef(null);
  const menuButtonRef = useRef(null);

  // The open collection is looked up in every collection the app knows, not
  // only the sidebar's: an admin may be in another account's.
  const current = collectionId ? (allInventories || inventories || []).find((inv) => String(inv.id) === collectionId) : null;
  const pendingCount = usePendingAccounts(isAdmin);
  const search = useShellSearch({ section, collectionId, onList, current });

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  // Leaving the page closes the drawer; growing to desktop does too.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia(DESKTOP);
    const onChange = () => { if (query.matches) setDrawerOpen(false); };
    if (query.addEventListener) query.addEventListener('change', onChange);
    return () => { if (query.removeEventListener) query.removeEventListener('change', onChange); };
  }, []);
  useModalFocus(drawerOpen, drawerRef, { onClose: closeDrawer, initialFocusRef: drawerForSearch ? search.inputRef : undefined });

  const openDrawer = (forSearch) => {
    setDrawerForSearch(forSearch);
    setDrawerOpen(true);
  };

  const focusSearch = useCallback(() => {
    if (isDesktop()) {
      if (search.inputRef.current) search.inputRef.current.focus();
      return;
    }
    // Below desktop a collection's page and the search page have their own box (the sidebar's is in the drawer).
    const pageBox = document.querySelector('[data-page-search]');
    if (pageBox && pageBox.offsetParent !== null) {
      pageBox.focus();
      if (pageBox.scrollIntoView) pageBox.scrollIntoView({ block: 'center' });
      return;
    }
    // Elsewhere, the search page with its own box, focused.
    if (section !== 'collection') {
      const to = { pathname: '/search' };
      if (mayNavigate(to)) navigate(to, { state: { focusSearch: true } });
      return;
    }
    setDrawerForSearch(true);
    setDrawerOpen(true);
  }, [search.inputRef, section, navigate]);

  // "/" focuses the search box, from anywhere but a text field, a dialog or an
  // open menu (its type-ahead has the key).
  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (typingInField(event.target) || document.body.classList.contains('has-modal')) return;
      if (document.querySelector('[role="menu"]')) return;
      event.preventDefault();
      focusSearch();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [focusSearch]);

  const canAddHere = current && (current.my_permission === 'owner' || current.my_permission === 'edit');
  // Readers scan too ("is it already here?"); the collection page keeps its own Add button.
  const scanHere = !!current && canCapture(current, canAddHere);
  // Films, series, books: one screen scans and photographs (§5.10).
  const captureHere = !!current && opensCapture(current, null, canAddHere);
  const add = () => {
    if (current && canAddHere) {
      // Through mayNavigate: an item form with unsaved edits asks first.
      const to = `/c/${current.id}/items/new`;
      // Already on a new item's form: nothing to do (and nothing to ask).
      if (location.pathname === to) return;
      if (mayNavigate(to)) navigate(to, { state: { fromCollection: onList } });
    }
    else if (current) toast.info(t('shell.readOnlyAdd', { name: current.name }));
    else onNewCollection();
  };

  const sectionTitle = current ? current.name
    : section === 'settings' ? t('shell.settings')
      : section === 'admin' ? t('shell.administration')
        : section === 'search' ? t('search.title')
          : t('shell.myCollections');

  // Phones and tablets: the open collection's saved views under it in the switcher (phase 3 §5.1).
  const { views } = useSavedViews(current ? current.id : null);
  const openView = current && onList ? activeView(views, location, current).view : null;
  const withViews = (inv) => (current && inv.id === current.id && views
    ? views.map((view) => ({
      id: `v${view.id}`, label: view.name, ariaLabel: t('views.switcherItem', { name: view.name, collection: inv.name }),
      icon: BookmarkIcon, nested: true, current: openView && openView.id === view.id,
      ...viewLink(inv.id, view),
    }))
    : []);
  const switcherItems = [
    ...ownedCollections(inventories).flatMap((inv) => [{ id: `c${inv.id}`, label: inv.name, to: `/c/${inv.id}` }, ...withViews(inv)]),
    ...(sharedCollections(inventories).length ? ['separator'] : []),
    ...sharedCollections(inventories).flatMap((inv) => [
      { id: `s${inv.id}`, label: t('shell.sharedItem', { name: inv.name, owner: inv.owner_name }), to: `/c/${inv.id}` }, ...withViews(inv)]),
    'separator',
    { id: 'all', label: t('shell.allCollections'), icon: GridIcon, to: '/' },
    { id: 'new', label: t('shell.newCollection'), icon: PlusIcon, onSelect: onNewCollection },
  ];

  const sidebarProps = {
    user, inventories, countOf, pendingCount, search, section, collectionId,
    onNewCollection: () => { closeDrawer(); onNewCollection(); },
    onInvite: () => { closeDrawer(); onInvite(); },
    onLogout,
  };

  return (
    <div className={`shell${drawerOpen ? ' shell--drawer-open' : ''}`}>
      <a className="shell-skip" href="#main-content">{t('shell.skipToContent')}</a>

      {/* Tablet and phone: top bar. */}
      <header className="shell-topbar">
        <IconButton ref={menuButtonRef} icon={MenuIcon} label={t('shell.openMenu')} variant="ghost"
                    className="shell-topbar__menu" aria-expanded={drawerOpen} aria-controls="shell-sidebar"
                    onClick={() => openDrawer(false)} />
        <div className="shell-topbar__logo"><Logo /></div>
        <div className="shell-topbar__switcher">
          <Menu
            label={t('shell.switchCollection')}
            items={switcherItems}
            trigger={(props) => (
              <button type="button" {...props} className="shell-switcher" aria-label={t('shell.switchCollectionFrom', { name: sectionTitle })}>
                <span className="shell-switcher__title">{sectionTitle}</span>
                <ChevronDownIcon size={18} strokeWidth={2.2} />
              </button>
            )}
          />
        </div>
        <IconButton icon={SearchIcon} label={search.label} variant="ghost" className="shell-topbar__search" onClick={focusSearch} />
      </header>

      {drawerOpen && <div className="shell-scrim" onClick={closeDrawer} aria-hidden="true" />}
      <aside
        id="shell-sidebar"
        ref={drawerRef}
        className="shell-sidebar"
        role={drawerOpen ? 'dialog' : undefined}
        aria-modal={drawerOpen ? 'true' : undefined}
        aria-label={drawerOpen ? t('shell.mainNav') : undefined}
        tabIndex={drawerOpen ? -1 : undefined}
      >
        <Sidebar {...sidebarProps} drawer={drawerOpen} onCloseDrawer={closeDrawer} onNavigate={closeDrawer} />
      </aside>

      <main id="main-content" className="shell-main" tabIndex={-1}>
        <div className="shell-content">{children}</div>
        {footer}
      </main>

      {/* Phone: bottom navigation. In a collection whose types hold ISBNs or EANs the scanner takes the centre
          (the canvas, phase 4 §5); elsewhere the centre adds. */}
      <nav className="shell-bottomnav" aria-label={t('shell.bottomNav')}>
        {/* Links, not buttons: an unsaved-edits guard sees them, and so does a new tab. */}
        <Link to="/" className={`shell-bottomnav__item${section === 'home' || section === 'collection' ? ' is-active' : ''}`}
              aria-current={section === 'home' ? 'page' : undefined}>
          <GridIcon size={22} />
          <span>{t('shell.bottom.collections')}</span>
        </Link>
        <button type="button" className="shell-bottomnav__item" onClick={focusSearch}>
          <SearchIcon size={22} />
          <span>{t('shell.bottom.search')}</span>
        </button>
        {scanHere ? (
          <Link to={scanPath(current.id)} className="shell-bottomnav__add" aria-label={t(captureHere ? 'capture.scanIn' : 'shell.scanIn', { name: current.name })}
                aria-current={[scanPath(current.id), `/c/${current.id}/covers`].includes(location.pathname) ? 'page' : undefined}>
            <ScanIcon size={26} strokeWidth={2.2} />
            <span aria-hidden="true">{captureHere ? t('capture.open') : t('shell.bottom.scan')}</span>
          </Link>
        ) : (
          <button type="button" className="shell-bottomnav__add" onClick={add}
                  aria-label={current ? t('shell.addItemTo', { name: current.name }) : t('shell.newCollection')}
                  aria-disabled={current && !canAddHere ? true : undefined}>
            <PlusIcon size={26} strokeWidth={2.2} />
            <span aria-hidden="true">{t('shell.bottom.add')}</span>
          </button>
        )}
        <Link to="/settings" className={`shell-bottomnav__item${section === 'settings' ? ' is-active' : ''}`}
              aria-current={section === 'settings' ? 'page' : undefined}>
          <SettingsIcon size={22} />
          <span>{t('shell.bottom.settings')}</span>
        </Link>
        <button type="button" className="shell-bottomnav__item" onClick={() => setAccountOpen(true)} aria-haspopup="dialog">
          <span className="shell-bottomnav__icon">
            <UserIcon size={22} />
            {pendingCount > 0 && <span className="shell-bottomnav__dot" aria-hidden="true" />}
          </span>
          <span>{t('shell.bottom.account')}</span>
        </button>
      </nav>

      <Dialog open={accountOpen} onClose={() => setAccountOpen(false)} title={t('shell.account')} size="sm">
        <div className="shell-account shell-account--sheet">
          <span className="shell-account__avatar" aria-hidden="true">{initials(user.display_name || user.email)}</span>
          <span className="shell-account__who">
            <span className="shell-account__name">{user.display_name}</span>
            <span className="shell-account__role">{user.email}</span>
          </span>
        </div>
        <div className="shell-sheet__row">
          <span>{t('language.label')}</span>
          <LanguageSwitcher />
        </div>
        <div className="shell-sheet__actions">
          {isAdmin && (
            <Button as={Link} to="/admin/accounts" variant="secondary" icon={ShieldIcon} block
                    aria-current={location.pathname === '/admin/accounts' ? 'page' : undefined}
                    onClick={() => setAccountOpen(false)}>
              {t('shell.administration')}
              {pendingCount > 0 && <Badge tone="accent" count>{pendingCount}</Badge>}
            </Button>
          )}
          <Button variant="secondary" icon={MailIcon} block onClick={() => { setAccountOpen(false); onInvite(); }}>
            {t('nav.invite')}
          </Button>
          <Button variant="secondary" icon={LogoutIcon} block onClick={() => { setAccountOpen(false); onLogout(); }}>
            {t('nav.logout')}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

export default AppShell;
